<template>
  <quick-info-popup
    v-if="simulation"
    class="simulationround-quickinfopopup"
    :position="{ x, y }"
    :info-icon-visible="false"
    :active="infoItem.active"
    style="width: 300px"
    @update:active="onActiveChange($event)"
    @update:position="onPositionChange($event)"
    @close="closePopup()"
    @pointerdown="refreshTimestamp()"
  >
    <template #label>{{ formatRoundIndex(roundIndex) }}</template>
    <template #title>{{ $t("headings.openRoundEffects") }}</template>

    <template #header-center>
      <!-- Filter buttons -->
      <div class="simulationround-quickinfopopup__filterbuttons">
        <button
          type="button"
          class="button-icon simulationround-quickinfopopup__filterbutton"
          :title="$t('objectNames.action', 2)"
          :class="{ '-selected': quickInfoEffectSources.action }"
          @click="toggleQuickInfoEffectSource('action')"
        >
          <span class="whitespace-nowrap">
            <span>{{ actionCount }}</span>
            <lucide-icon
              name="action"
              class="simulationround-quickinfopopup__filtericon"
            />
          </span>
        </button>
        <button
          v-if="features.scheduledEffect && simulation.useScheduledEffects"
          type="button"
          class="button-icon simulationround-quickinfopopup__filterbutton"
          :title="$t('objectNames.scheduledEffect', 2)"
          :class="{ '-selected': quickInfoEffectSources.scheduledEffect }"
          @click="toggleQuickInfoEffectSource('scheduledEffect')"
        >
          <span class="whitespace-nowrap">
            <span>{{ scheduledEffectCount }}</span>
            <lucide-icon
              name="impulse"
              class="simulationround-quickinfopopup__filtericon"
            />
          </span>
        </button>
        <button
          type="button"
          class="button-icon simulationround-quickinfopopup__filterbutton"
          :title="$t('objectNames.incident', 2)"
          :class="{ '-selected': quickInfoEffectSources.incident }"
          @click="toggleQuickInfoEffectSource('incident')"
        >
          <span class="whitespace-nowrap">
            <span>{{ incidentCount }}</span>
            <lucide-icon
              name="incident"
              class="simulationround-quickinfopopup__filtericon"
            />
          </span>
        </button>
      </div>
    </template>

    <template #default>
      <p
        v-if="
          (roundImpulses && roundImpulses.length == 0) ||
          roundEffects.length == 0
        "
        class="message-info mv-0"
      >
        {{ $t("messages.noEffects") }}
      </p>

      <zebra-grid>
        <!-- TODO: hide roundimpulses if effects hinted? -->
        <template v-if="roundImpulses">
          <template
            v-for="impulseValue in roundImpulses"
            :key="impulseValue.affectedType + impulseValue.affectedId"
          >
            <div class="quickinfo-popup__griditem">
              <store-item
                :id="impulseValue.affectedId"
                v-slot="{ item: effectItem }"
                :type="impulseValue.affectedType"
                class="quickinfo-popup__item"
              >
                {{ effectItem.title }}
              </store-item>
            </div>
            <div class="quickinfo-popup__griditem"></div>
            <div class="quickinfo-popup__griditem">
              {{ $n(impulseValue.value, "displaySign") }}
            </div>
            <div class="quickinfo-popup__griditem"></div>
          </template>
        </template>
        <template v-else-if="roundEffects.length > 0">
          <effect-expression
            v-for="effect in roundEffects"
            :key="effect.key"
            v-slot="{ value, affectedItem }"
            :effect-id="'---'"
            :expression="effect.impulseExpressions[0]"
            :rounds="rounds"
            :round-index="roundIndex"
            :parse-context="{
              affectedType: effect.affectedType,
              affectedId: effect.affectedId,
              targets,
              resources,
              indices
            }"
            :sign-required="true"
          >
            <div class="quickinfo-popup__griditem">
              <store-item
                :id="effect.affectedId"
                v-slot="{ item: effectItem }"
                :type="effect.affectedType"
                class="quickinfo-popup__item"
              >
                {{ effectItem.title }}
              </store-item>
            </div>
            <div class="quickinfo-popup__griditem"></div>
            <div class="quickinfo-popup__griditem">
              <template v-if="effect.visibility == 'hint' || effect.visibility == 'mixed'">
                {{ effect.hint }}
              </template>
              <template v-if="effect.visibility == 'visible' || effect.visibility == 'mixed'">
                <template v-if="effect.visibility == 'mixed' && effect.hint">,</template>
                {{ value }}
              </template>
            </div>
            <div class="quickinfo-popup__griditem">
              <template v-if="effect.visibility == 'visible' || effect.visibility == 'mixed'">
                {{ affectedItem?.unit }}
              </template>
            </div>
          </effect-expression>
        </template>
      </zebra-grid>
    </template>
  </quick-info-popup>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ensureInt } from "@/helpers/utility/Type";

import { formatRoundName } from "@editor/items/simulationRound/useRoundTime";

import ZebraGrid from "@core/ui/ZebraGrid.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import QuickInfoPopup from "@core/ui/QuickInfoPopup.vue";

import StoreItem from "@editor/items/StoreItem.vue";
import EffectExpression from "@editor/items/effect/EffectExpression.vue";

import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useSelectionStore } from "@editor/store/editor/selection.store";
import { useTargetStore } from "../target/targets.store";
import { useResourceStore } from "../resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useFeatures } from "@editor/store/features.store";

import type { JoinedBaseEffectItem } from "../effect/EffectItem";

import type { SimulationRoundQuickInfo } from "@editor/views/simulation/simulationView.store";

const { infoItem } = defineProps<{
  infoItem: SimulationRoundQuickInfo;
}>();
const emit = defineEmits<{
  (e: "close", value: SimulationRoundQuickInfo): void;
}>();

const features = useFeatures();

const simulationViewStore = useSimulationViewStore();

const simulation = computed(() => simulationViewStore.simulation);
const scenario = computed(() => simulationViewStore.scenario);
const targets = computed(() => simulationViewStore.targets);
const resources = computed(() => simulationViewStore.resources);
const rounds = computed(() => simulationViewStore.rounds);
const quickInfoEffectSources = computed(
  () => simulationViewStore.quickInfoEffectSources
);

const x = computed(() => {
  return infoItem.x;
});

const y = computed(() => {
  return infoItem.y;
});

const roundIndex = computed(() => {
  return infoItem.roundIndex;
});

const round = computed(() => {
  return rounds.value?.[roundIndex.value];
});

const activeRoundEffectSources = computed(() => {
  type SourceKey = keyof typeof quickInfoEffectSources.value;
  const sources: SourceKey[] = [];
  let type: SourceKey;
  for (type in quickInfoEffectSources.value) {
    if (quickInfoEffectSources.value[type]) sources.push(type);
  }
  return sources;
});

const roundEffects = computed((): JoinedBaseEffectItem[] => {
  if (scenario.value?.effectsVisibility == "hidden") return [];

  const openRoundEffects = simulationViewStore.getJoinedOpenRoundEffects(
    roundIndex.value,
    [...activeRoundEffectSources.value, "pendingEffect"]
  );

  const effects = Object.values(openRoundEffects);

  // sort target and resource effects by item order
  const targetOrder = useTargetStore().order as number[];
  const resourceOrder = useResourceStore().order as number[];

  effects.sort((a, b) => {
    if (a.affectedType == b.affectedType) {
      return (
        (a.affectedType == "target"
          ? targetOrder.indexOf(a.affectedId)
          : resourceOrder.indexOf(a.affectedId)) -
        (b.affectedType == "target"
          ? targetOrder.indexOf(b.affectedId)
          : resourceOrder.indexOf(b.affectedId))
      );
    }
    if (a.affectedType == "resource") return -1;
    return 1;
  });

  const reducedEffects = effects.map((effect): JoinedBaseEffectItem => {
    const expressions = effect.impulseExpressions;
    const reducedExpression = expressions.reduce((expressionA, expressionB) => {
      if (!expressionB) return expressionA;
      if (!expressionA) return expressionB;

      if (expressionB.startsWith("=")) {
        if (expressionA.startsWith("=")) return expressionB;
        return `${expressionB}${expressionA}`;
      }
      if (expressionA.startsWith("-")) {
        return `+(${expressionA})${expressionB}`;
      }
      return `${expressionA}${expressionB}`;
    }, "");
    return {
      ...effect,
      impulseExpressions: [reducedExpression]
    }
  });

  // filter out effects without effect
  const effectiveEffects = reducedEffects.filter((effect) => {
    const expression = effect.impulseExpressions[0];
    if (expression == null || expression === "+0" || expression == "")
      return false;
    return true;
  });

  if (scenario.value?.effectsVisibility == "values-hidden") {
    return effectiveEffects.map((effect) => ({
      ...effect,
      visibility: "hint"
    }));
  }
  if (scenario.value?.effectsVisibility == "visible") {
    return effectiveEffects.map((effect) => ({
      ...effect,
      visibility: "visible"
    }));
  }

  return effectiveEffects;
});

const roundImpulses = computed(() => {
  if (!round.value?.played) return null;

  const impulseValues: {
    value: number;
    affectedId: number;
    affectedType: "target" | "resource";
  }[] = [];
  const impulses = round.value.impulses;

  let affectedType: "target" | "resource";
  for (affectedType in impulses) {
    for (const affectedId in impulses[affectedType]) {
      const impulse = impulses[affectedType][affectedId];

      // make sure this is one of the affected items
      if (
        !affectedItems.value.find(
          (i) => i.type == affectedType && `${i.id}` == affectedId
        )
      ) {
        continue;
      }

      const value = impulse.sourceValues.reduce((sum, sourceValue) => {
        const type = sourceValue.type;
        if (type in quickInfoEffectSources.value) {
          if (
            quickInfoEffectSources.value[
              type as "scheduledEffect" | "action" | "incident"
            ] == true
          ) {
            sum += sourceValue.value || 0;
          }
        }
        return sum;
      }, 0);

      if (value != 0) {
        impulseValues.push({
          value,
          affectedId: ensureInt(affectedId),
          affectedType
        });
      }
    }
  }

  const orders = {
    target: useTargetStore().order,
    resource: useResourceStore().order
  };

  impulseValues.sort((a, b) => {
    if (a.affectedType == b.affectedType) {
      return (
        orders[a.affectedType].indexOf(a.affectedId) -
        orders[b.affectedType].indexOf(b.affectedId)
      );
    }
    if (a.affectedType == "resource") return -1;
    return 1;
  });

  return impulseValues;
});

const affectedItems = computed(() => {
  return roundEffects.value.map((effect) => {
    return { id: effect.affectedId, type: effect.affectedType };
  });
});

const incidentCount = computed(() => {
  return round.value?.activatedIncidents?.length || 0;
});
const actionCount = computed(() => {
  return (
    simulation.value?.actionPlans?.[roundIndex.value]?.plannedActionIds
      ?.length || 0
  );
});
const scheduledEffectCount = computed(() => {
  return (
    simulation.value?.actionPlans?.[roundIndex.value]?.scheduledEffects
      ?.length || 0
  );
});
const indices = computed(() => {
  return useIndexStore().items;
});

function closePopup() {
  onActiveChange(false);
  emit("close", infoItem);
}
function onActiveChange(active: boolean) {
  if (active) {
    update({
      lastUpdate: Date.now(),
      active
    });
    for (const affectedItem of affectedItems.value) {
      useSelectionStore().setHighlight(affectedItem);
    }
  } else {
    update({
      active
    });
    for (const affectedItem of affectedItems.value) {
      useSelectionStore().removeHighlight(affectedItem);
    }
  }
}
function formatRoundIndex(roundIndex: number) {
  if (simulation.value == null) return roundIndex + 1;
  return formatRoundName(
    simulation.value.durationType,
    simulation.value.roundUnit,
    simulation.value.roundLength,
    roundIndex,
    simulation.value.timeStart
  );
}
function update(data: Partial<SimulationRoundQuickInfo>) {
  simulationViewStore.updateSimulationQuickInfo({
    ...data,
    roundIndex: infoItem.roundIndex,
    type: "round"
  });
}
function refreshTimestamp() {
  update({
    lastUpdate: Date.now()
  });
}

function onPositionChange({ x, y }: { x: number; y: number }) {
  update({ x, y });
}
function toggleQuickInfoEffectSource(
  type: "scheduledEffect" | "incident" | "action"
) {
  simulationViewStore.set({
    quickInfoEffectSources: {
      ...quickInfoEffectSources.value,
      [type]: !quickInfoEffectSources.value[type]
    }
  });
}
</script>

<style lang="scss">
.simulationround-quickinfopopup {
  min-width: 200px;

  &__filterbuttons {
    display: flex;
    align-self: flex-start;
    margin-top: 0.3rem;
    box-shadow: 0 1px 2px 1px var(--color-shadow-1);
    overflow: hidden;
    border-radius: 5px;
  }
  &__filterbutton {
    border: none;
    background: none;
    padding: 0.5rem;
    font-size: 14px;
    border-radius: 0;

    & + & {
      border-left: 1px solid var(--color-grey);
    }
  }

  &__filtericon {
    font-size: 12px;
  }
}
</style>
